import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';

export const budgetTotalsSchema = z.object({
  budgetAmount: z.number(),
  disburseAmount: z.number(),
});

export const mpAdjustmentRowSchema = z.object({
  promoItemId: z.coerce.number(),
  itemNo: z.string(),
  itemCode: z.string().nullable(),
  name: z.string(),
  distributedAmount: z.number(),
});

export const mpAdjustmentResponseSchema = z.object({
  data: z.array(mpAdjustmentRowSchema),
  meta: paginationMetaSchema,
});

export const mpSummaryRowSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  hrEmpId: z.string().nullable(),
  userMobile: z.string().nullable(),
  designationName: z.string().nullable(),
  departmentName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  budgetAmount: z.number(),
  disburseAmount: z.number(),
  approvedAmount: z.number(),
  notApprovedAmount: z.number(),
  duesAmount: z.number(),
});

export const mpSummaryResponseSchema = z.object({
  data: z.array(mpSummaryRowSchema),
  meta: paginationMetaSchema.extend({ totals: budgetTotalsSchema }),
});

export const mpRegionRowSchema = z.object({
  regionId: z.coerce.number(),
  regionName: z.string(),
  sideRegion: z.string().nullable(),
  rsmUserId: z.coerce.number(),
  rsmName: z.string(),
  smName: z.string().nullable(),
  distributedAmount: z.number(),
});

export const mpRegionListSchema = z.array(mpRegionRowSchema);

export const mpApprovalRowSchema = z.object({
  detailId: z.coerce.number(),
  recordId: z.coerce.number(),
  dcrDate: z.string(),
  promoItemId: z.coerce.number(),
  itemName: z.string(),
  claimedAmount: z.number(),
  approvedAmount: z.number(),
  approveTypeNo: z.number(),
  teacherName: z.string().nullable(),
  teacherMobile: z.string().nullable(),
  instituteId: z.coerce.number().nullable(),
  instituteCode: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  instituteEiin: z.string().nullable(),
  instituteThana: z.string().nullable(),
  instituteDistrict: z.string().nullable(),
  enteredById: z.coerce.number().nullable(),
  enteredByName: z.string().nullable(),
  enteredByMobile: z.string().nullable(),
  enteredAt: z.string().nullable(),
  recommendedBy: z.string().nullable(),
  recommendedComments: z.string().nullable(),
  approvalComments: z.string().nullable(),
  paymentType: z.string().nullable(),
  paymentPlace: z.string().nullable(),
  givenDate: z.string().nullable(),
  totalStudent: z.number().nullable(),
  approxSaleQty: z.number().nullable(),
  witness: z.string().nullable(),
  transactionRef: z.string().nullable(),
  sourceOfFund: z.coerce.number().nullable(),
  callStatus: z.number().nullable(),
  calledBy: z.string().nullable(),
  callTime: z.string().nullable(),
  teacherFeedback: z.number().nullable(),
  isMisVerified: z.boolean(),
  hasAttachment: z.boolean(),
});

export const mpApprovalResponseSchema = z.object({
  data: z.array(mpApprovalRowSchema),
  meta: paginationMetaSchema,
});

export type BudgetTotals = z.infer<typeof budgetTotalsSchema>;
export type MpAdjustmentRow = z.infer<typeof mpAdjustmentRowSchema>;
export type MpAdjustmentResponse = z.infer<typeof mpAdjustmentResponseSchema>;
export type MpSummaryRow = z.infer<typeof mpSummaryRowSchema>;
export type MpSummaryResponse = z.infer<typeof mpSummaryResponseSchema>;
export type MpRegionRow = z.infer<typeof mpRegionRowSchema>;
export type MpApprovalRow = z.infer<typeof mpApprovalRowSchema>;
export type MpApprovalResponse = z.infer<typeof mpApprovalResponseSchema>;

export const mpReportSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  userId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
  promoItemId: z.coerce.number().optional(),
  itemCode: z.string().optional(),
});

export const mpRegionSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  rsmUserId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
  promoItemId: z.coerce.number().optional(),
  itemCode: z.string().optional(),
});

const numberList = z.preprocess(
  (value) => (value === undefined ? undefined : Array.isArray(value) ? value : [value]),
  z.array(z.coerce.number()),
);

export const mpApprovalSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  enteredBy: z.coerce.number().optional(),
  enteredMobile: z.string().optional(),
  instituteId: z.coerce.number().optional(),
  instituteEiin: z.string().optional(),
  teacherMobile: z.string().optional(),
  promoItemIds: numberList.optional(),
  approveTypeNos: numberList.optional(),
  callStatus: z.coerce.number().optional(),
  isMisVerified: z.boolean().optional(),
});

export type MpReportSearch = z.infer<typeof mpReportSearchSchema>;
export type MpRegionSearch = z.infer<typeof mpRegionSearchSchema>;
export type MpApprovalSearch = z.infer<typeof mpApprovalSearchSchema>;

export const decideDonationFormSchema = z.object({
  approvedAmount: z.string().trim().min(1, 'An amount is required'),
  approvalComments: z.string().trim().max(2000, 'Comment must be 2000 characters or fewer'),
  paymentType: z.string().trim().max(500),
  paymentPlace: z.string().trim().max(500),
  transactionRef: z.string().trim().max(500),
  witness: z.string().trim().max(500),
  givenDate: z.string().trim(),
});

export type DecideDonationFormValues = z.infer<typeof decideDonationFormSchema>;

export interface DecideDonationPayload {
  approveTypeNo: number;
  approvedAmount: number;
  approvalComments?: string;
  paymentType?: string;
  paymentPlace?: string;
  transactionRef?: string;
  witness?: string;
  givenDate?: string;
}

export interface VerifyMisPayload {
  callStatus?: number;
  teacherFeedback?: number;
  misComment?: string;
}
